import { Badge } from '@playerbase/ui';
import {
  resolveBinaryLabels,
  resolveRatingDisplay,
} from '@playerbase/types';

import type { TimelineResponseCard } from '../../games/playtest-sessions-data.js';

interface Props {
  card: TimelineResponseCard;
}

// For kinds that store a canonical value distinct from the player-facing
// label (binary 'true'/'false', emoji-mode rating), surface "Label (raw)"
// so studios see both what the player saw and the value that ended up in
// the feedback pool. Kinds where the raw text is already the label
// (select / severity / nps / text) fall through to plain rawText.
function formatResponse(card: TimelineResponseCard): string {
  const raw = card.rawText;
  if (card.questionKind === 'binary' && card.questionConfig !== null) {
    const v = raw.toLowerCase();
    if (v === 'true' || v === 'false') {
      const { trueLabel, falseLabel } = resolveBinaryLabels(card.questionConfig);
      return `${v === 'true' ? trueLabel : falseLabel} (${raw})`;
    }
  }
  if (card.questionKind === 'rating' && card.questionConfig !== null) {
    const display = resolveRatingDisplay(card.questionConfig);
    if (display?.mode === 'emoji' && /^[1-5]$/.test(raw)) {
      const n = Number.parseInt(raw, 10);
      return `${display.emoji.repeat(n)} (${raw})`;
    }
  }
  return raw;
}

// Response card renders the answer inline. We indent + soften the
// chrome (no transparency, no accent borders) so it reads as a child
// of the dispatch it answered rather than a peer of the event /
// dispatch cards above it. A future PR can wire a hyperlink to the
// Feedback page when that page learns to deep-link to a pool record id.
export function SessionResponseCard({ card }: Props): JSX.Element {
  return (
    <div className="ml-6 border-l-2 border-border pl-3">
      <div className="flex flex-col gap-1.5 rounded-md border border-border bg-surface-2 px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Badge tone="info">Response</Badge>
            {card.questionPrompt !== null ? (
              <span className="text-sm text-text">{card.questionPrompt}</span>
            ) : (
              <span className="text-sm text-text-muted">(question removed)</span>
            )}
          </div>
          <span className="text-xs text-text-muted">{formatTime(card.at)}</span>
        </div>
        <p className="whitespace-pre-wrap text-sm text-text">{formatResponse(card)}</p>
      </div>
    </div>
  );
}

function formatTime(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleString();
}
